import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { INDIAN_STATES } from 'src/app/private/constants/indian-states';
import { AutoCompleteContactSearchComponent } from 'src/app/private/modules/components/auto-complete-contact-search/auto-complete-contact-search.component';
import { AutoCompleteSearchComponent } from 'src/app/private/modules/components/auto-complete-search/auto-complete-search.component';
import { QuoteService } from 'src/app/private/services/quote.service';
import { DealService } from 'src/app/private/services/deals.service';
import { CompanyService } from 'src/app/private/services/company.service';
import { ProductService } from 'src/app/private/services/product.service';
import { CategoryService } from 'src/app/private/services/category.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { TrackingService } from 'src/app/private/services/tracking.service';
import { ManageBusinessInfoService } from 'src/app/private/services/manage-business-info.service';

@Component({
  selector: 'app-create-quote',
  templateUrl: './create-quote.component.html',
  styleUrls: ['./create-quote.component.scss']
})
export class CreateQuoteComponent implements OnInit {

  @Input() formAction: string = 'Add';
  @Input() editData: any = {};
  @Input() users: any[] = [];
  @Output() close: EventEmitter<any> = new EventEmitter();
  @Input() deals: any[] = [];
  @Output() reload: EventEmitter<any> = new EventEmitter();

  btnLoading = false;
  sendEmailLoading = false;
  readonly postalCodePattern = '^[A-Za-z0-9][A-Za-z0-9\\s\\-]{2,11}$';
  accounts: any[] = [];
  quote: any = {
    quote_number: '',
    assign_to: this.localStorage.getUserId(),
    status: 'draft',
    quote_date: new Date().toISOString().split('T')[0],
    subject: '',
    terms_conditions: '',
    valid_until: '',
    currency: 'USD',
    subtotal: 0,
    tax_amount: 0,
    discount_amount: 0,
    total_amount: 0,
    notes: '',
    created_by: null,
    account_id: null,
    deal_id: null,
    deal_name: '',
    account_name: '',
    client_id: null,
    address: {
      billingStreet: '',
      billingCity: '',
      billingState: '',
      /** GST state code (2-digit string), set from billing state dropdown — stored in quote.address JSON */
      billingStateCode: '',
      billingCode: '',
      billingCountry: '',
      shippingStreet: '',
      shippingCity: '',
      shippingState: '',
      shippingStateCode: '',
      shippingCode: '',
      shippingCountry: ''
    },
    items: [
      {
        category_id: '',
        product_id: '',
        productName: '',
        quantity: 1,
        unit_price: 0,
        amount: 0,
        discount_rate: 0,
        tax_rate: 0,
        total_price: 0,
        description: '',
        products: [],
        loadingProducts: false
      }
    ]
  };

  currencies = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD'];

  /** Same list as manage-business-info (GST state codes). */
  indianStates = INDIAN_STATES;

  /** Subtotal + discount rows; Total Tax / GST split / Total Amount are rendered explicitly in the template. */
  totalsBeforeTax = [
    { label: 'Subtotal', model: 'subtotal' },
    { label: 'Discount', model: 'discount_amount' }
  ];

  /** Client GST state code from business profile (compare to quote billing state for CGST+SGST vs IGST display). */
  clientGstStateCode = '';

  currencyCode: any = 'INR';

  @ViewChild('autoCompleteAccount') autoCompleteAccount!: AutoCompleteSearchComponent;
  @ViewChild('autoCompleteContact') autoCompleteContact!: AutoCompleteContactSearchComponent;

  isAdmin: boolean = false;
  formSubmitted: boolean = false;

  today = new Date().toISOString().split('T')[0];

  products: any[] = [];
  categories: any[] = [];

  constructor(
    private api: QuoteService,
    private dealService: DealService,
    private companyService: CompanyService,
    private productService: ProductService,
    private categoryService: CategoryService,
    private toast: ToastrService,
    private localStorage: LocalStorageService,
    private tracker: TrackingService,
    private manageBusinessInfo: ManageBusinessInfoService
  ) {
    // Set current user as created_by
    this.quote.created_by = this.localStorage.getUserId();
    this.quote.client_id = this.localStorage.getClientId();

    this.isAdmin = this.localStorage.checkFullAccessForTableList(
      this.localStorage.getRole()
    );
  }


  ngOnInit() {
    if (this.formAction === 'Update' && this.editData) {
      this.populateEditData();
    }
    this.generateQuoteNumber();
    this.currencyCode = this.localStorage.getClientCurrency();
    
    // First load categories
    this.loadCategories();
    this.loadClientGstStateCode();
  }

  private loadClientGstStateCode(): void {
    const clientId = this.localStorage.getClientId();
    if (!clientId) {
      return;
    }
    this.manageBusinessInfo.getClient({ client_id: clientId }).subscribe({
      next: (res: any) => {
        const d = res?.data ?? res;
        this.clientGstStateCode = String(d?.gst_state_code ?? '').trim();
      },
      error: () => {
        this.clientGstStateCode = '';
      }
    });
  }

  /** Normalize 2-digit GST state codes for comparison (e.g. "9" vs "09"). */
  normalizeGstStateCode(code: string | null | undefined): string {
    const s = String(code ?? '').trim();
    if (!s) {
      return '';
    }
    const n = parseInt(s, 10);
    if (!Number.isFinite(n) || n < 0) {
      return s;
    }
    return n.toString().padStart(2, '0');
  }

  canShowGstSplit(): boolean {
    return !!this.normalizeGstStateCode(this.clientGstStateCode)
      && !!this.normalizeGstStateCode(this.quote.address?.billingStateCode);
  }

  /** Same state → intra-state (CGST + SGST); different → inter-state (IGST). */
  isIntraStateGst(): boolean {
    return (
      this.normalizeGstStateCode(this.clientGstStateCode)
      === this.normalizeGstStateCode(this.quote.address?.billingStateCode)
    );
  }

  getCgstDisplay(): number {
    const t = Number(this.quote.tax_amount) || 0;
    return Math.round((t / 2) * 100) / 100;
  }

  getSgstDisplay(): number {
    const t = Number(this.quote.tax_amount) || 0;
    const cgst = this.getCgstDisplay();
    return Math.round((t - cgst) * 100) / 100;
  }

  getIgstDisplay(): number {
    return Number(this.quote.tax_amount) || 0;
  }

  loadCategories() {
    const params = {
      client_id: this.localStorage.getClientId()
    };

    this.categoryService.getAllCategory(params).subscribe({
      next: (response: any) => {
        if (response) {
          this.categories = response;
        } else {
          this.categories = [];
        }
      },
      error: (error: any) => {
        console.error('Error loading categories:', error);
        this.toast.error('Failed to load categories');
      }
    });
  }

  onCategorySelectedForRow(event: any, rowIndex: number) {
    const categoryId = event.target.value;
    const item = this.quote.items[rowIndex];
    
    // Reset product selection and related fields
    item.product_id = '';
    item.productName = '';
    item.unit_price = 0;
    item.description = '';
    item.products = [];
    
    if (categoryId) {
      this.loadProductsForRow(categoryId, rowIndex);
    }
    
    // Recalculate totals after resetting the row
    this.updateTotals();
  }

  loadProductsForRow(categoryId: string, rowIndex: number) {
    const item = this.quote.items[rowIndex];
    item.loadingProducts = true;
    
    const params: any = {
      "page": 0,
      "limit": 10000,
      where: {
        client_id: this.localStorage.getClientId(),
        cat_id: categoryId
      },
      "order": {
        "col": "\"product\".\"updatedAt\"",
        "dir": "DESC"
      }
    };

    this.productService.getAllProduct(params).subscribe({
      next: (response: any) => {
        item.loadingProducts = false;
        if (response && response.rows) {
          item.products = response.rows.map((p: any) => ({
            product_id: p.product_id,
            product_name: p.product_name,
            price: parseFloat(p.unit_price),
            description: p.description,
            tax: p.tax
          }));
        } else {
          item.products = [];
        }
      },
      error: (error: any) => {
        console.error('Error loading products:', error);
        item.loadingProducts = false;
        item.products = [];
        this.toast.error('Failed to load products');
      }
    });
  }



  loadAccounts() {
    const params = {
      client_id: this.localStorage.getClientId(),
      page: 1,
      limit: 1000
    };

    this.companyService.getAllAccount(params).subscribe({
      next: (response: any) => {
        if (response && response.accounts) {
          this.accounts = response.accounts;
        }
      },
      error: (error: any) => {
        console.error('Error loading accounts:', error);
        this.toast.error('Failed to load accounts');
      }
    });
  }

  onDealSelected(event: any) {
    const dealId = event.target.value;
    const selectedDeal = this.deals.find(deal => deal.deal_id == dealId);
    if (selectedDeal) {
      this.quote.deal_id = selectedDeal.deal_id;
      this.quote.deal_name = selectedDeal.deal_name;
    } else {
      this.quote.deal_id = null;
      this.quote.deal_name = '';
    }
  }

  onAccountSelectedFromDropdown(event: any) {
    const accountId = event.target.value;
    const selectedAccount = this.accounts.find(account => account.account_id == accountId);
    if (selectedAccount) {
      this.quote.account_id = selectedAccount.account_id;
      this.quote.account_name = selectedAccount.account_name;
    } else {
      this.quote.account_id = null;
      this.quote.account_name = '';
    }
  }

  populateEditData() {
    // Map quote items to match the component's format with category support
    const mappedItems = this.editData.quote_item?.map((item: any) => ({
      category_id: String(item.cat_id ?? item.category_id ?? ''),
      product_id: String(item.product_id ?? ''),
      productName: item.product?.product_name || item.name || '',
      quantity: Number(item.quantity ?? 1),
      unit_price: Number(item.unit_price ?? item.price ?? 0),
      amount:
        Number(item.unit_price ?? item.price ?? 0) *
        Number(item.quantity ?? 1),
      discount_rate: Number(item.discount_rate ?? item.discount ?? 0),
      tax_rate: Number(item.tax_rate ?? item.tax ?? 0),
      total_price: Number(item.total_price ?? item.total ?? 0),
      description: item.description || '',
      products: [],
      loadingProducts: false
    })) || [this.quote.items[0]];

    const mergedAddress = {
      ...this.quote.address,
      ...(this.editData.address || {})
    };
    this.quote = {
      ...this.quote,
      ...this.editData,
      assign_to: String(
        this.editData.assign_to ?? this.localStorage.getUserId() ?? ''
      ),
      deal_id:
        this.editData.deal_id == null ? null : String(this.editData.deal_id),
      account_id: this.editData.account_id ?? this.editData.account?.account_id ?? null,
      account_name:
        this.editData.account?.account_name || this.editData.account_name || '',
      currency:
        this.editData.currency || this.localStorage.getClientCurrency() || 'INR',
      quote_date: this.editData.quote_date
        ? new Date(this.editData.quote_date).toISOString().split('T')[0]
        : this.quote.quote_date,
      valid_until: this.editData.valid_until ? new Date(this.editData.valid_until).toISOString().split('T')[0] : '',
      status: this.editData.status || 'draft',
      address: mergedAddress,
      items: mappedItems
    };
    this.quote.address.billingCode = this.sanitizePostalCodeValue(this.quote.address.billingCode);
    this.quote.address.shippingCode = this.sanitizePostalCodeValue(this.quote.address.shippingCode);
    this.updateTotals();
    this.syncBillingStateCodeFromName();

    // Load products for each item that has a category
    this.quote.items.forEach((item: any, index: number) => {
      if (item.category_id) {
        this.loadProductsForRow(item.category_id, index);
      }
    });
  }

  generateQuoteNumber() {
    if (this.formAction === 'Add') {
      const timestamp = Date.now().toString().slice(-6);
      this.quote.quote_number = `QT${timestamp}`;
    }
  }

  addRow() {
    this.quote.items.push({
      category_id: '',
      product_id: '',
      productName: '',
      quantity: 1,
      unit_price: 0,
      listPrice: 0,
      amount: 0,
      discount_rate: 0,
      tax_rate: 0,
      total_price: 0,
      description: '',
      products: [],
      loadingProducts: false
    });
  }

  removeRow(index: number) {
    if (this.quote.items.length > 1) {
      this.quote.items.splice(index, 1);
      this.updateTotals();
    }
  }

  onBillingStateChange(stateName: string): void {
    const match = this.indianStates.find((s) => s.name === stateName);
    this.quote.address.billingStateCode = match ? match.code : '';
  }

  /** When loading a quote, derive GST code from stored state name if present. */
  syncBillingStateCodeFromName(): void {
    const name = String(this.quote.address?.billingState || '').trim();
    if (!name) {
      this.quote.address.billingStateCode = '';
      return;
    }
    const match = this.indianStates.find((s) => s.name === name);
    this.quote.address.billingStateCode = match ? match.code : this.quote.address.billingStateCode || '';
  }

  copyAddress() {
    this.quote.address.shippingStreet = this.quote.address.billingStreet;
    this.quote.address.shippingCity = this.quote.address.billingCity;
    this.quote.address.shippingState = this.quote.address.billingState;
    this.quote.address.shippingStateCode = this.quote.address.billingStateCode;
    this.quote.address.shippingCode = this.sanitizePostalCodeValue(this.quote.address.billingCode);
    this.quote.address.shippingCountry = this.quote.address.billingCountry;

    this.toast.success('Address copied to shipping address');
  }

  sanitizePostalCodeValue(value: unknown): string {
    return String(value ?? '')
      .replace(/[^A-Za-z0-9\s\-]/g, '')
      .replace(/^\s*-+/, '');
  }

  /** Block minus at the start of postal/PIN codes (negative numbers). Hyphens mid-value remain allowed. */
  onPostalCodeKeyDown(event: KeyboardEvent): void {
    if (event.key !== '-' && event.key !== 'Subtract') {
      return;
    }
    const input = event.target as HTMLInputElement;
    const pos = input.selectionStart ?? 0;
    const before = input.value.slice(0, pos);
    if (!before.replace(/\s/g, '').length) {
      event.preventDefault();
    }
  }

  sanitizePostalCode(field: 'billingCode' | 'shippingCode', event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = this.sanitizePostalCodeValue(input.value);
    if (sanitized !== input.value) {
      input.value = sanitized;
    }
    this.quote.address[field] = sanitized;
  }

  isPostalCodeValid(value: unknown, required = false): boolean {
    const normalized = this.sanitizePostalCodeValue(value).trim();
    if (!normalized) {
      return !required;
    }
    return new RegExp(this.postalCodePattern).test(normalized);
  }

  hasValidAddress(): boolean {
    const a = this.quote.address;
    const billingOk = [
      a.billingStreet,
      a.billingCity,
      a.billingState,
      a.billingCode,
      a.billingCountry
    ].every((v) => String(v ?? '').trim() !== '');
    const billingGstOk = String(a.billingStateCode ?? '').trim() !== '';
    const billingPostalOk = this.isPostalCodeValid(a.billingCode, true);
    const shippingPostalOk = this.isPostalCodeValid(a.shippingCode, false);
    return billingOk && billingGstOk && billingPostalOk && shippingPostalOk;
  }

  updateTotals() {
    let subtotal = 0;
    let totalTax = 0;
    let totalDiscount = 0;

    this.quote.items.forEach((item: any) => {
      // Ensure numeric values
      item.quantity = Number(item.quantity) || 0;
      item.unit_price = Number(item.unit_price) || 0;
      item.discount_rate = Number(item.discount_rate) || 0;
      item.tax_rate = Number(item.tax_rate) || 0;

      // Calculate row totals
      item.amount = item.quantity * item.unit_price;
      const discountAmount = (item.amount * item.discount_rate) / 100;
      const taxAmount = ((item.amount - discountAmount) * item.tax_rate) / 100;
      item.total_price = Number((item.amount - discountAmount + taxAmount).toFixed(2));

      // Add to quote totals
      subtotal += item.amount;
      totalTax += taxAmount;
      totalDiscount += discountAmount;
    });

    // Update quote totals with 2 decimal precision
    this.quote.subtotal = Number(subtotal.toFixed(2));
    this.quote.discount_amount = Number(totalDiscount.toFixed(2));
    this.quote.tax_amount = Number(totalTax.toFixed(2));
    this.quote.total_amount = Number((subtotal - totalDiscount + totalTax).toFixed(2));
  }

  onAccountSelected(account: any) {
    console.log("Selected account:", account);
    this.quote.account_id = account?.account_id;
    this.quote.account_name = account?.account_name;

    console.log("Account selected:", this.quote.account_id, this.quote.account_name);
  }

  onContactSelected(contact: any) {
    this.quote.client_id = contact?.id;
  }

  onProductSelected(event: any, rowIndex: number) {
    const productId = event.target.value;
    const item = this.quote.items[rowIndex];
    const selectedProduct = item.products.find((p: any) => p.product_id == productId);

    if (selectedProduct) {
      console.log('Selected Product:', selectedProduct);
      
      // Calculate total tax percentage from product's tax array
      let totalTaxRate = 0;
      if (selectedProduct.tax && Array.isArray(selectedProduct.tax)) {
        console.log('Tax Array:', selectedProduct.tax);
        totalTaxRate = selectedProduct.tax.reduce((sum: number, taxItem: any) => {
          // Extract percentage value from item_text (e.g., "CGST - 9.00%" -> 9.00)
          if (taxItem.item_text) {
            const match = taxItem.item_text.match(/(\d+(?:\.\d+)?)\s*%/);
            if (match && match[1]) {
              const taxValue = parseFloat(match[1]);
              console.log(`Extracted tax from "${taxItem.item_text}": ${taxValue}%`);
              return sum + taxValue;
            }
          }
          return sum;
        }, 0);
      }
      
      console.log('Total Tax Rate Calculated:', totalTaxRate);
      
      item.productName = selectedProduct.product_name;
      item.unit_price = selectedProduct.price || 0;
      item.description = selectedProduct.description || '';
      item.tax_rate = totalTaxRate;
      
      // Ensure quantity is at least 1 for calculation
      if (!item.quantity || item.quantity === 0) {
        item.quantity = 1;
      }
      
      console.log('Updated Item:', item);
      this.updateTotals();
    }
  }

  hasValidItems(): boolean {
    return this.quote.items && this.quote.items.length > 0 &&
      this.quote.items.some((item: any) => item.product_id && item.product_id !== '');
  }

  validateForm(form: NgForm): boolean {
    // Mark form as submitted to trigger error message display
    Object.keys(form.controls).forEach(key => {
      form.controls[key].markAsTouched();
    });
    
    // Check form validity
    if (!form.valid) {
      return false;
    }

    // Check if account is selected (since it's required)
    if (!this.quote.account_id) {
      return false;
    }

    // Check if at least one item with product name exists
    if (!this.hasValidItems()) {
      return false;
    }

    if (!this.hasValidAddress()) {
      return false;
    }

    return true;
  }

  async saveAndSendQuote(form: NgForm) {
    // Mark form as submitted to show validation errors
    this.formSubmitted = true;
    form.form.markAllAsTouched();
    
    if (!this.validateForm(form)) {
      this.toast.error('Please fill all required fields, complete billing address, and add at least one item!');
      return;
    }

    const emailProviderType = this.localStorage.getEmailProviderTypeForPayload();
    const fromEmail = this.localStorage.getSendEmailFromAddress();
    if (!emailProviderType || !fromEmail) {
      this.toast.error(
        'Set an email preference or connect an Outlook/Gmail account before using save & send.'
      );
      return;
    }

    this.sendEmailLoading = true;

    try {
      const saveRes: any = await this.saveQuote(form, true);

      console.log('Quote saved before sending email:', saveRes);

      // After decrypt: usually { success: true, data: quote }. Legacy: full Lambda { body: JSON string }.
      let quoteId: number | undefined =
        this.formAction === 'Add' ? undefined : this.editData?.id;
      if (this.formAction === 'Add') {
        let row = saveRes?.data;
        if ((!row || row.id == null) && typeof saveRes?.body === 'string') {
          try {
            const inner = JSON.parse(saveRes.body);
            row = inner?.data ?? inner;
          } catch {
            /* ignore */
          }
        }
        if (row && row.id != null) {
          quoteId = Number(row.id);
        }
      }

      if (quoteId == null || !Number.isFinite(quoteId)) {
        this.toast.error('Could not resolve quote id after save. Cannot send email.');
        this.sendEmailLoading = false;
        this.btnLoading = false;
        return;
      }

      const emailData = {
        id: quoteId,
        client_id: this.localStorage.getClientId(),
        companyName: this.quote.account_name || this.editData?.account?.account_name,
        emailProviderType,
        from_email: fromEmail,
      };

      this.api.sendQuote(emailData).subscribe({
        next: () => {
          this.toast.success('Quote Created and sent successfully!');
          this.reload.emit();
          this.sendEmailLoading = false;
          this.btnLoading = false;
          this.closeModal();
        },
        error: (error: any) => {
          console.error('Error sending quote:', error);
          this.toast.error(
            error.error?.message || 'Error sending quote email. Please try again.'
          );
          this.sendEmailLoading = false;
          this.btnLoading = false;
        },
      });
    } catch (error) {
      console.error('Error in save and send process:', error);
      this.toast.error('Error in save and send process. Please try again.');
      this.btnLoading = false;
      this.sendEmailLoading = false;
    }
  }

  saveQuote(form: NgForm, skipClose: boolean = false) {
    // Mark form as submitted to show validation errors
    this.formSubmitted = true;
    form.form.markAllAsTouched();
    
    if (!this.validateForm(form)) {
      this.toast.error('Please fill all required fields, complete billing address, and add at least one item!');
      return Promise.reject('Form validation failed');
    }

    if (this.sendEmailLoading === false) {
      this.btnLoading = true;
    }

    const quoteData = {
      quote_number: this.quote.quote_number,
      assign_to: this.quote.assign_to,
      status: this.quote.status || 'draft',
      quote_date: new Date(this.quote.quote_date),
      subject: this.quote.subject,
      terms_conditions: this.quote.terms_conditions,
      valid_until: new Date(this.quote.valid_until),
      currency: this.quote.currency,
      subtotal: this.quote.subtotal,
      tax_amount: this.quote.tax_amount,
      discount_amount: this.quote.discount_amount,
      total_amount: this.quote.total_amount,
      notes: this.quote.notes,
      created_by: this.quote.created_by,
      account_id: this.quote.account_id,
      deal_id: this.quote.deal_id || null,
      client_id: this.quote.client_id,
      address: this.quote.address,
      quote_item: (this.quote.items || []).map((item: any) => ({
        cat_id: item.category_id,
        product_id: item.product_id,
        description: item.description,
        name : item.productName,
        quantity: item.quantity,
        price: item.unit_price,
        tax: item.tax_rate,
        discount: item.discount_rate,
        total: item.total_price
      }))
    };


    if (this.formAction === 'Add') {
      return new Promise((resolve, reject) => {
        this.api.createQuote(quoteData).subscribe({
          next: (res: any) => {
            console.log('Quote created:', res);

            const desc = `Quote created with number: ${this.quote.quote_number}`;
            this.tracker.track("Create", "Quote", desc);

            if (!skipClose) {
              this.btnLoading = false;
              this.reload.emit();
              this.closeModal();
              this.toast.success('Quote Created Successfully!');
            }
            resolve(res);
          },
          error: (error: any) => {
            console.error('Error creating quote:', error);
            this.toast.error('Error creating quote. Please try again.');
            this.btnLoading = false;
            reject(error);
          }
        });
      });
    } else {
      const updateData = {
        ...quoteData,
        id: this.editData.id,
        client_id: this.localStorage.getClientId(),
        status: 'draft',
        user_id: this.localStorage.getUserId()
      };

      return new Promise((resolve, reject) => {
        this.api.updateQuote(updateData).subscribe({
          next: (res: any) => {
            console.log('Quote updated:', res);
            this.toast.success('Quote Updated Successfully!');

            const desc = `Quote updated with number: ${this.quote.quote_number}`;
            this.tracker.track("Update", "Quote", desc);

            this.reload.emit();
            if (!skipClose) {
              this.closeModal();
            }
            this.btnLoading = false;
            resolve(res);
          },
          error: (error: any) => {
            console.error('Error updating quote:', error);
            this.toast.error('Error updating quote. Please try again.');
            this.btnLoading = false;
            reject(error);
          }
        });
      });
    }
  }

  closeModal(): void {
    this.close.emit();
  }


}